/**
 * <biosphere-cart-items> — cart updates for the markup rendered by
 * snippets/biosphere-cart-items.liquid. Behaviour: reference/specs/cart.md.
 *
 * Quantity changes debounce, then hit the platform change API with the section ids of
 * every cart surface on the page, so one response carries fresh HTML for all of them.
 * Errors revert the input and show the platform's message verbatim on that line.
 * Every successful change publishes `biosphere:cart:change` on document; surfaces
 * that were not the source re-render themselves.
 */
import { sectionUrl, sectionsUrlContext } from "biosphere-section-url";

const DEBOUNCE_MS = 300;

class BiosphereCartItems extends HTMLElement {
  #abort;
  #debounce;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener(
      "biosphere:quantity:change",
      (event) => this.#onQuantityChange(event),
      { signal },
    );
    this.addEventListener("click", (event) => this.#onClick(event), {
      signal,
    });
    document.addEventListener(
      "biosphere:cart:change",
      (event) => this.#onExternalChange(event),
      { signal },
    );

    // Back/forward navigation serves this page as it was server-rendered, so a buyer who
    // adds on a product page and presses Back sees the old count. It arrives two ways,
    // needing different signals:
    //
    //   bfcache      the page is resumed, scripts do NOT re-run, and only pageshow fires
    //                with persisted.
    //   HTTP cache   the page is re-parsed and scripts DO re-run, but pageshow says
    //                persisted: false — the navigation entry is what identifies it.
    window.addEventListener("pageshow", (event) => this.#onPageShow(event), {
      signal,
    });
    this.#resyncIfRestored();

    this.#hideNoJsAffordances();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#debounce);
  }

  /** Swap this surface's content from a Section Rendering API response. */
  applyHtml(html) {
    if (!html) return;
    const fetched = new DOMParser().parseFromString(html, "text/html");
    const fresh = fetched.querySelector("biosphere-cart-items");
    if (!fresh) return;
    this.replaceChildren(...fresh.childNodes);
    this.#runScripts();
    this.#hideNoJsAffordances();
  }

  /**
   * A parsed-then-inserted <script> never runs — the spec marks scripts created by the
   * parser as already-started, so cloning them into the document is inert. Each line
   * brings its own component's script with it, so on a page that has not already loaded
   * one, that component stays un-upgraded: the quantity stepper renders its buttons
   * `hidden` for the no-JS baseline and waits for a script that never arrives.
   *
   * That is why this only showed up when adding from a landing page. A product page has
   * already loaded biosphere-quantity for its buy box, and an empty drawer ships no
   * line items and therefore no script to load it.
   *
   * Re-creating the element is what marks it un-started, so the browser runs it. A
   * module already evaluated under the same URL is not evaluated twice, and every
   * component guards its own `customElements.define`, so repeated swaps are safe.
   */
  #runScripts() {
    for (const stale of this.querySelectorAll("script")) {
      const script = document.createElement("script");
      for (const { name, value } of stale.attributes) {
        script.setAttribute(name, value);
      }
      script.textContent = stale.textContent;
      stale.replaceWith(script);
    }
  }

  #hideNoJsAffordances() {
    const update = this.querySelector("[data-nojs-update]");
    if (update) update.hidden = true;
  }

  // The keys of a row and of any gifts given with it. Returns null when the
  // row has no gifts, which is the ordinary case and keeps the plain
  // line-number path untouched.
  #keysFor(host) {
    const giftKeys = (host?.dataset.giftKeys ?? "")
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean);
    if (!giftKeys.length || !host?.dataset.key) return null;
    return [host.dataset.key, ...giftKeys];
  }

  #onQuantityChange(event) {
    const { line, value } = event.detail ?? {};
    if (!line) return;
    // Debounce so stepper clicking settles into one request; quantity
    // zero means remove.
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => {
      // A gift is one per unit of the thing it came with, so its quantity
      // follows rather than being set on its own — two tubs bought, two
      // gifts. Both lines move in one write, keyed, so the cart is never
      // momentarily out of step.
      const keys = this.#keysFor(this.querySelector(`[data-line="${line}"]`));
      if (keys) {
        this.#setByKey(keys, value);
        return;
      }
      this.#change(line, value);
    }, DEBOUNCE_MS);
  }

  #onClick(event) {
    const remove = event.target.closest(".biosphere-cart-items__remove");
    if (!remove) return;
    const host = remove.closest("[data-line]");
    const line = Number.parseInt(host?.dataset.line ?? "", 10);
    if (!line) return;
    event.preventDefault();

    // A gift goes when the line it came with goes — left behind it would sit
    // in the cart as a full-price item nobody chose. Zero is just the
    // quantity case where nothing is left.
    const keys = this.#keysFor(host);
    if (keys) {
      this.#setByKey(keys, 0);
      return;
    }
    this.#change(line, 0);
  }

  // Addressed by line item key, never by position. A line number is only true
  // until the next write — /cart/change.js renumbers the cart after every
  // call, so a second write by number can hit whatever slid into that slot. A
  // key belongs to one line and nothing else, and /cart/update.js takes
  // several in one request, so the lines move together with no window in
  // between where they disagree.
  async #setByKey(keys, quantity) {
    const url = this.dataset.cartUpdateUrl;
    if (!url) return;

    this.setAttribute("aria-busy", "true");

    const sections = [...document.querySelectorAll("biosphere-cart-items")]
      .map((surface) => surface.dataset.sectionId)
      .filter(Boolean);

    const updates = {};
    for (const key of keys) updates[key] = quantity;

    let payload;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          updates,
          sections: sections.join(","),
          sections_url: sectionsUrlContext(),
        }),
      });
      payload = await response.json();
    } catch {
      this.removeAttribute("aria-busy");
      return;
    }
    this.removeAttribute("aria-busy");

    this.#applyAndPublish(payload);
  }

  async #change(line, quantity) {
    this.setAttribute("aria-busy", "true");

    const sections = [...document.querySelectorAll("biosphere-cart-items")]
      .map((surface) => surface.dataset.sectionId)
      .filter(Boolean);

    let payload;
    try {
      const response = await fetch(this.dataset.cartChangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: sections.join(","),
          sections_url: sectionsUrlContext(),
        }),
      });
      payload = await response.json();
    } catch {
      this.removeAttribute("aria-busy");
      return;
    }
    this.removeAttribute("aria-busy");

    const errorMessage =
      payload.errors ?? (payload.status ? payload.message : null);
    if (errorMessage) {
      this.#showLineError(line, errorMessage);
      // Totals may have drifted from the optimistic input; republish the
      // real cart so every surface re-syncs.
      const cart = await this.#fetchCart();
      if (cart) this.#publish(cart, "update");
      return;
    }

    this.#applyAndPublish(payload);
  }

  // Swap every cart surface from the one response, then announce the cart.
  // Shared by both write paths so they cannot drift apart.
  #applyAndPublish(payload) {
    const applied = [];
    for (const surface of document.querySelectorAll("biosphere-cart-items")) {
      const html = payload.sections?.[surface.dataset.sectionId];
      if (surface !== this && html) {
        surface.applyHtml(html);
        applied.push(surface.dataset.sectionId);
      }
    }
    this.applyHtml(payload.sections?.[this.dataset.sectionId]);

    this.#publish(payload, "update", applied);
  }

  #showLineError(line, message) {
    const row = this.querySelector(`[data-line="${line}"]`);
    const input = row?.querySelector(".biosphere-quantity__input");
    // defaultValue is the server-rendered value attribute — the last state
    // the platform accepted.
    if (input) input.value = input.defaultValue;

    const error = row?.querySelector("[data-line-error]");
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    const live = this.querySelector(".biosphere-cart-items__live");
    if (live) live.textContent = message;
  }

  async #fetchCart() {
    try {
      const response = await fetch(`${this.dataset.cartUrl}.json`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      return await response.json();
    } catch {
      return null;
    }
  }

  #publish(cart, action, sectionsApplied = [], source) {
    document.dispatchEvent(
      new CustomEvent("biosphere:cart:change", {
        detail: {
          cart,
          action,
          source: source ?? this.dataset.cartItemsId,
          sectionsApplied,
        },
      }),
    );
  }

  #onPageShow(event) {
    if (event.persisted) this.#resync();
  }

  #resyncIfRestored() {
    const [navigation] = performance.getEntriesByType("navigation");
    if (navigation?.type === "back_forward") this.#resync();
  }

  async #resync() {
    let cart;
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      cart = await response.json();
    } catch {
      return;
    }

    // A source no surface answers to, so every one of them re-renders —
    // including this one, whose own markup is as stale as the rest.
    this.#publish(cart, "refresh", [], "pageshow");
  }

  async #onExternalChange(event) {
    const { source, sectionsApplied } = event.detail ?? {};
    if (source === this.dataset.cartItemsId) return;
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    // Already refreshed from the change response's bundled sections.
    if (sectionsApplied?.includes(sectionId)) return;

    // Busy for the whole fetch, not just for changes this surface starts:
    // the drawer marks itself busy the moment an add begins, and this is
    // what releases it once the fresh cart is on screen.
    this.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(
        // The page's own query string is kept and `sections` added to it. Built
        // from pathname alone, this asked the server for a different page than
        // the one being viewed: /collections/all?view=black-friday re-rendered
        // as /collections/all, and anything else carried in the URL — a filter,
        // a page number, the preview token — went with it.
        sectionUrl(sectionId),
        { cache: "no-store" },
      );
      const data = await response.json();
      this.applyHtml(data[sectionId]);
    } catch {
      // A failed refresh leaves the current (stale but consistent) state;
      // next interaction re-syncs.
    }
    this.removeAttribute("aria-busy");
  }
}

if (!customElements.get("biosphere-cart-items")) {
  customElements.define("biosphere-cart-items", BiosphereCartItems);
}
